import { TGetOrderResponse } from '@p2p/services';
import { hasUrlScheme, isCardPaymentLinkOrder } from '@p2p/shared/functions';
import { FC } from 'react';
import QRCode from 'react-qr-code';

import styles from './PayByLink.module.scss';

type PayByLinkProps = {
  order: TGetOrderResponse;
};

/**
 * P2P-2808: оплата по ссылке (QR СБП) для провайдеров, у которых реквизита в
 * привычном виде нет.
 *
 * Провайдер (сейчас PRAGMATICA, методы mobcom/sbp) отдаёт только ссылку на QR
 * СБП; номер счёта, телефона и ФИО в реквизите — технические ЗАГЛУШКИ, которые
 * бэкенд генерирует сам. Показывать их плательщику нельзя: на проде 09.09.2026
 * страница вывела заглушечный номер +7 911 132 45 54, по которому человек мог
 * попытаться перевести деньги.
 *
 * Признак — булев флаг `payment_via_link` из check_order. Именно флаг, а НЕ
 * provider_type: со страницы оплаты не должно быть видно, какой провайдер выдал
 * реквизит (решение P2P-2101), поэтому правило вычисляется на бэкенде.
 *
 * Ссылка приходит в ios_link / android_link (бэкенд дублирует в оба поля один и
 * тот же qrCodeLink — см. P2P-2790). В отличие от OpenBankAppButton, кнопка
 * показывается и на десктопе тоже: по ТЗ там она открывает ту же ссылку, а
 * пользователь сканирует QR камерой телефона.
 *
 * P2P-2849 (MeridianPay, индонезийский QR): провайдер отдаёт не ссылку, а сам
 * QRIS-пейлоад ("000201…"), и бэкенд кладёт в ios_link / android_link его.
 * Кнопка с таким href никуда не ведёт (на проде 23.09.2026: «куара нет, а при
 * нажатии оплатить не работает»), поэтому для значения без URL-схемы вместо
 * кнопки рисуем QR-код — плательщик сканирует его приложением банка.
 *
 * P2P-2915 (SimplePay, метод ecom): по ссылке открывается не QR, а форма оплаты
 * банковской картой на странице провайдера (бэкенд присылает
 * payment_link_type = 'card'). Кнопка та же, второй шаг инструкции — про
 * оплату картой, а не про сканирование QR.
 */
export const PayByLink: FC<PayByLinkProps> = ({ order }) => {
  const link = order.ios_link || order.android_link;

  if (!link) {
    return null;
  }

  if (!hasUrlScheme(link)) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.qr}>
          <QRCode value={link} size={200} />
        </div>
        <ol className={styles.instruction}>
          <li>Откройте приложение банка и выберите оплату по QR-коду</li>
          <li>Отсканируйте QR-код</li>
        </ol>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <a className={styles.button} href={link} rel="noopener noreferrer">
        Оплатить
      </a>
      <ol className={styles.instruction}>
        <li>Нажмите на кнопку «Оплатить»</li>
        {isCardPaymentLinkOrder(order) ? (
          <li>Откроется страница оплаты картой — введите данные карты и подтвердите платёж.</li>
        ) : (
          <li>Отсканируйте QR-код в мобильном приложении банка или штатной камерой телефона.</li>
        )}
      </ol>
    </div>
  );
};
